import { DB } from '@services/database.service';
import { EVENT_TYPES } from '@/shared/constants/event-types.v4';
import { calculateDurationShift, prepareEventClone, prepareReschedule, analyzeNormalizationNeeds } from '@/shared/logic/event-logic';
import { MetabolicCompute } from '@/shared/logic/metabolic-logic';
import { Event } from '@/types/zod.schemas';
import { toLogicDate } from '@/utils/date-utils';

export interface EventManagerBackup {
    timestamp: string;
    user_id: string;
    events: any[];
    sleep_logs: any[];
}

/**
 * EVENT MANAGER SERVICE
 * Centralized logic for managing the lifecycle and synchronization of tactical events.
 * HARDENED: Mutation methods now use Optimistic UI updates to ensure 0ms latency.
 * V5.7: Delegates persistence preparation to MetabolicCompute.flattenPayloadForDB.
 */
export const EventManager = {
    /**
     * performEmergencyBackup: Dumps critical tables to local JSON for safety.
     */
    async performEmergencyBackup(userId: string): Promise<EventManagerBackup> {
        try {
            const [events, sleepLogs] = await Promise.all([
                DB.fetchEvents(userId),
                DB.fetchSleepHistory(userId)
            ]);

            return {
                timestamp: new Date().toISOString(),
                user_id: userId,
                events: events.data || [],
                sleep_logs: sleepLogs.data || []
            };
        } catch (err) {
            console.error("[EventManager] Backup FAILED:", err);
            throw err;
        }
    },

    /**
     * runSleepNormalization: Standardizes recovery data by processing compute-driven actions.
     */
    async runSleepNormalization(userId: string): Promise<{ success: boolean }> {
        try {
            const { data: events } = await DB.fetchEvents(userId);
            if (!events) return { success: true };

            const actions = analyzeNormalizationNeeds(events);
            
            for (const action of actions) {
                if (action.type === 'UPDATE') {
                    await DB.updateUnifiedEvent(action.id, action.payload);
                } else if (action.type === 'DELETE') {
                    await DB.deleteUnifiedEvent(action.id);
                }
            }

            // Sync UI
            const { useHistoryStore } = await import('@stores/v4/useHistoryStore');
            await useHistoryStore.getState().fetchGlobalHistory(true);

            return { success: true };
        } catch (err) {
            console.error("[EventManager] Normalization FAILED:", err);
            throw err;
        }
    },

    calculateDurationShift,

    /**
     * resolvePerformanceMeta: Pure metadata extractor.
     */
    resolvePerformanceMeta(log: any) {
        return {
            exerciseName: log.block_items?.exercise_library?.name || log.exercise_name_snapshot || 'Unknown Exercise',
            targets: {
                weight: log.block_items?.target_weight || log.target_snapshot || '?',
                reps: log.block_items?.target_reps || '?',
                rpe: log.block_items?.target_rpe || '?'
            }
        };
    },

    /**
     * saveEvent: Master IO bridge for persistence.
     * HARDENED: Delegates payload flattening to logic layer for DB compatibility.
     */
    async saveEvent(event: any) {
        const { data: { user } } = await DB.getUser();
        if (!user) throw new Error("User not authenticated");

        const startTimestamp = new Date(`${event.startDate}T${event.startTime}:00`).toISOString();
        const endTimestamp = new Date(`${event.endDate}T${event.endTime}:00`).toISOString();

        // 1. Prepare database-compliant payload using shared logic
        const cleanPayload = MetabolicCompute.flattenPayloadForDB(event.payload);

        const dbPayload: any = {
            user_id: user.id,
            type: event.type,
            status: event.status,
            start_time: startTimestamp,
            end_time: endTimestamp,
            label: event.title || event.type,
            program_day_id: event.programDayId,
            payload: cleanPayload
        };

        // 2. Prepare Preview Data (Flattened for aggregation & AI querying)
        if (event.type === EVENT_TYPES.NUTRITION) {
            const totals = cleanPayload.items ? MetabolicCompute.calculateLedgerTotals(cleanPayload.items) : null;
            dbPayload.preview_data = {
                macros: {
                    calories: MetabolicCompute.resolveMetricValue(cleanPayload.calories),
                    protein: MetabolicCompute.resolveMetricValue(cleanPayload.protein),
                    carbs: MetabolicCompute.resolveMetricValue(cleanPayload.carbs),
                    fat: MetabolicCompute.resolveMetricValue(cleanPayload.fat),
                    insulin_load: MetabolicCompute.resolveMetricValue(cleanPayload.insulin_load)
                },
                micros: totals ? Object.fromEntries(
                    Object.entries(totals.micros).map(([k, v]) => [k, { amount: v.total, unit: v.unit }])
                ) : {}
            };
        } else if (event.type === EVENT_TYPES.SUPPLEMENTATION || event.type === 'SUPPLEMENT') {
            const totals = cleanPayload.items ? MetabolicCompute.calculateLedgerTotals(cleanPayload.items) : null;
            dbPayload.preview_data = {
                micros: totals ? Object.fromEntries(
                    Object.entries(totals.micros).map(([k, v]) => [k, { amount: v.total, unit: v.unit }])
                ) : {}
            };
        }

        if (event.type === EVENT_TYPES.RECOVERY && event.payload) {
            dbPayload.preview_data = {
                quality_rating: event.payload.quality_rating,
                notes: event.payload.notes
            };
        }

        // OPTIMISTIC SYNC
        const { useHistoryStore } = await import('@stores/v4/useHistoryStore');
        if (event.id) {
            useHistoryStore.getState().updateOptimisticEvent(event.id, dbPayload);
        }

        let result;
        if (event.id) {
            result = await DB.updateUnifiedEvent(event.id, dbPayload);
        } else {
            result = await DB.createUnifiedEvent(dbPayload);
            if (result.data) {
                useHistoryStore.getState().addOptimisticEvent(result.data);
            }
        }
        if (result.error) throw result.error;

        if (event.type === EVENT_TYPES.RECOVERY && event.sleep_log_id) {
            await DB.updateSleepLog(event.sleep_log_id, {
                start_time: startTimestamp,
                end_time: endTimestamp,
                quality_rating: event.payload?.quality_rating,
                notes: event.payload?.notes
            });
            await DB.registerRecoveryEvent(event.sleep_log_id, event.id || result.data.id);
        }

        if (event.type === EVENT_TYPES.PERFORMANCE && event.completed_session_id) {
            await DB.updateCompletedSession(event.completed_session_id, {
                start_time: startTimestamp,
                end_time: endTimestamp
            });

            if (event.performanceLogs && event.performanceLogs.length > 0) {
                for (const log of event.performanceLogs) {
                    await DB.updatePerformanceLog(log.id, {
                        weight: log.weight,
                        reps: log.reps,
                        rpe: log.rpe
                    });
                }
            }

            await DB.registerPerformanceEvent(event.completed_session_id, event.id || result.data.id);
        }

        return result;
    },

    /**
     * deleteCitizenEvent: Unified deletion logic.
     * HARDENED: Uses optimistic removal.
     */
    async deleteCitizenEvent(eventId: string, subId?: string, type?: string) {
        const { useHistoryStore } = await import('@stores/v4/useHistoryStore');
        useHistoryStore.getState().removeOptimisticEvent(eventId);

        if (type === EVENT_TYPES.RECOVERY && subId) {
            await DB.deleteSleepLog(subId);
        } else if (type === EVENT_TYPES.PERFORMANCE && subId) {
            await DB.deleteSessionRecord(subId);
        }
        
        await DB.deleteUnifiedEvent(eventId);
    },

    /**
     * cloneEvent: Deep copies an event with date-shifting.
     * HARDENED: Robust date handling to prevent 'invalid date' errors.
     */
    async cloneEvent(event: any, targetDate: string | Date) {
        const { data: { user } } = await DB.getUser();
        if (!user) throw new Error("User not authenticated");

        const dateStr = typeof targetDate === 'string' ? targetDate : toLogicDate(targetDate);
        const clonePayload = prepareEventClone(event, dateStr, user.id);
        const res = await DB.createUnifiedEvent(clonePayload);

        if (res.data) {
            const { useHistoryStore } = await import('@stores/v4/useHistoryStore');
            useHistoryStore.getState().addOptimisticEvent(res.data);
        }

        return res;
    },

    /**
     * rescheduleEvent: Moves an event to a new date while preserving its original time.
     * HARDENED: Performs optimistic update for instant UI feedback.
     */
    async rescheduleEvent(eventId: string, targetDate: string | Date) {
        const { useHistoryStore } = await import('@stores/v4/useHistoryStore');
        const history = useHistoryStore.getState();
        const event = history.events.find(e => e.id === eventId);
        if (!event) return;

        const dateStr = typeof targetDate === 'string' ? targetDate : toLogicDate(targetDate);
        const updates = prepareReschedule(event, dateStr);

        // 1. Optimistic Update
        history.updateOptimisticEvent(eventId, updates);

        // 2. Persistent Update
        return await DB.updateUnifiedEvent(eventId, updates);
    },

    /**
     * exportSessionData: Generates tactical JSON download.
     */
    exportSessionData(session: any) {
        if (!session || !session.groupedLogs) return;

        const durationMinutes = session.start_time
            ? Math.round((new Date(session.end_time).getTime() - new Date(session.start_time).getTime()) / 60000)
            : 'Unknown';

        const exportData = {
            mission_brief_for_ai: "Analyze this training session. Compare actual Weight/Reps/RPE against targets. Identify performance leakage in tempo or duration. Determine if progressive overload was achieved.",
            session_metadata: {
                label: session.program_days?.label || session.label || 'Activity',
                date: new Date(session.end_time || session.start_time).toLocaleDateString(),
                start_time: session.start_time,
                end_time: session.end_time,
                duration_minutes: durationMinutes
            },
            exercise_data: session.groupedLogs.map((group: any) => ({
                exercise_name: group.name,
                targets: group.targets,
                actual_sets: (group.sets || []).map((s: any) => ({
                    weight: s.weight, reps: s.reps, rpe: s.rpe, set_number: s.set_number
                }))
            }))
        };

        const dateObj = new Date(session.end_time || session.start_time);
        const fileName = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}-${(session.program_days?.label || session.label || 'Workout').replace(/\s+/g, '-')}.json`;

        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url; link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }
};
